import InputAdornment from '@mui/material/InputAdornment';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import { LoadingButton } from 'src/components/buttons/LoadingButton';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import EmailIcon from '@mui/icons-material/Email';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import { useEffect, useState } from 'react';
import { GradientTypography } from '../components/gradients/GradientTypography';
import { Link } from '../components/Link';
import validator from 'validator';
import { GradientBorder } from 'src/components/gradients/GradientBorder';

function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [contactValues, setContactValues] = useState({
    fullName: '',
    email: '',
    message: '',
  });
  const [errors, setErrors] = useState({
    fullName: false,
    email: false,
    message: false,
  });
  const handleChange = (stateName, event) => {
    setContactValues({ ...contactValues, [stateName]: event.target.value });
  };
  const handleSubmit = async () => {
    const newErrors = {};
    for (const [name, value] of Object.entries(contactValues)) {
      if (!value) {
        newErrors[`${name}`] = true;
        setErrors((prev) => {
          return {
            ...prev,
            [name]: true,
          };
        });
      }
    }
    if (
      Object.values(newErrors).some((error) => error) ||
      Object.values(errors).some((error) => error)
    ) {
      console.error(errors);
      return;
    }
    const { fullName, email, message } = contactValues;
    const formData = new FormData();
    formData.append('name', fullName);
    formData.append('email', email);
    formData.append('message', message);
    await fetch('https://getform.io/f/558e25d0-52fd-456b-aeba-d578bc7e1a66', {
      method: 'POST',
      body: formData,
    });
    // window.open(
    //   `mailto:info@blumint.io?body=${message}&subject=${`Message from ${fullName}, <${email}>`}`,
    // );
    setSubmitted(true);
  };
  const validateName = (name: string) => {
    if (name.length > 3) {
      return setErrors((prev) => {
        return { ...prev, fullName: false };
      });
    }
    return setErrors((prev) => {
      return { ...prev, fullName: true };
    });
  };
  const validateMessage = (message: string) => {
    if (message.length > 20) {
      return setErrors((prev) => {
        return { ...prev, message: false };
      });
    }
    return setErrors((prev) => {
      return { ...prev, message: true };
    });
  };
  const validateEmail = (email: string) => {
    if (!validator.isEmail(email)) {
      return setErrors((prev) => {
        return { ...prev, email: true };
      });
    }
    return setErrors((prev) => {
      return { ...prev, email: false };
    });
  };
  useEffect(() => {
    if (contactValues.email) {
      validateEmail(contactValues.email);
    }
  }, [contactValues.email]);
  useEffect(() => {
    if (contactValues.fullName) {
      validateName(contactValues.fullName);
    }
  }, [contactValues.fullName]);
  useEffect(() => {
    if (contactValues.message) {
      validateMessage(contactValues.message);
    }
  }, [contactValues.message]);
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        pt: 15,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          mb: 10,
          px: 2,
        }}
      >
        <GradientTypography
          sx={{ mb: 5 }}
          variant="h2"
          gradientColor="primary.vertical"
        >
          Contact Us
        </GradientTypography>
        <Typography variant="h6">
          {`We'd love to hear from you! Get in touch on `}
          <Link href="https://twitter.com/blumint_">Twitter</Link>,{' '}
          <Link href="https://discord.gg/QfcJRBkrGX">Discord</Link>, or by using
          the form below.
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', maxWidth: '600px', width: '100%' }}>
        {!submitted ? (
          <GradientBorder borderRadius="10px" sx={{ width: '100%' }}>
            <FormControl
              sx={{
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '10px',
                gap: 8,
                background: theme.palette.background.elevation[2],
                px: 4,
                py: 6,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  gap: 4,
                  '& .MuiInputAdornment-root': {
                    color: theme.palette.text.secondary,
                  },
                  '& .Mui-focused': {
                    '& .MuiInputAdornment-root': {
                      color: theme.palette.primary.main,
                    },
                  },
                  '& .Mui-error': {
                    '& .MuiInputAdornment-root': {
                      color: theme.palette.error.main,
                    },
                  },
                }}
              >
                <TextField
                  variant="outlined"
                  color="primary"
                  label="Full Name"
                  value={contactValues.fullName}
                  error={errors.fullName}
                  onChange={(e) => handleChange('fullName', e)}
                  helperText={
                    errors.fullName ? 'Please enter your full name' : ' '
                  }
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccountCircleIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'primary.dark',
                      },
                    },
                  }}
                />
                <TextField
                  required
                  variant="outlined"
                  color="primary"
                  label="Email"
                  value={contactValues.email}
                  error={errors.email}
                  onChange={(e) => handleChange('email', e)}
                  helperText={
                    errors.email
                      ? 'Please enter a valid email'
                      : 'We will never share your email.'
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'primary.dark',
                      },
                    },
                  }}
                />
              </Box>
              <Box
                sx={{
                  width: '100%',
                  '& .MuiInputAdornment-root': {
                    color: theme.palette.text.secondary,
                  },
                  '& .Mui-focused': {
                    '& .MuiInputAdornment-root': {
                      color: theme.palette.primary.main,
                    },
                  },
                  '& .Mui-error': {
                    '& .MuiInputAdornment-root': {
                      color: theme.palette.error.main,
                    },
                  },
                }}
              >
                <TextField
                  required
                  variant="outlined"
                  color="primary"
                  multiline
                  label="Message"
                  fullWidth
                  onChange={(e) => handleChange('message', e)}
                  value={contactValues.message}
                  error={errors.message}
                  helperText={
                    errors.message
                      ? contactValues.message.length === 0
                        ? 'Please enter a message'
                        : 'Please enter a longer message'
                      : ' '
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ChatBubbleIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'primary.dark',
                      },
                    },
                  }}
                />
              </Box>
              <LoadingButton
                onClick={handleSubmit}
                variant="outlined"
                sx={{ height: 40 }}
              >
                Submit
              </LoadingButton>
            </FormControl>
          </GradientBorder>
        ) : (
          <Typography variant="h4" sx={{ my: 20, textAlign: 'center' }}>
            Thank you for your message! We will get back to you as soon as
            possible.
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export { ContactPage as default };
